import {
  Button,
  Divider,
  IconButton,
  Modal,
  Switch,
  TextField,
  Typography,
  Alert,
  Box,
  Paper
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useContext, useEffect, useState, useCallback, useMemo } from 'react'

import { encrypt, validPasswordFormat } from '../../utils/AppUtils'
import { apiConfig } from '../../api/ApiConfig'
import { UserDetailContext } from '../../context/UserDetailContext'

const { getPublicKey, updateProfile } = apiConfig()

const ProfileModal = ({ isModalOpen, handleModalClose }) => {
  const userDetailContext = useContext(UserDetailContext)
  
  // Initial state constants
  const initialPasswordState = useMemo(() => ({
    resetPassword: false,
    newPassword: '',
    confirmPassword: ''
  }), [])
  
  const initialProfileState = useMemo(() => ({
    hasUpdate: false,
    newUserDetail: userDetailContext.userDetail
  }), [userDetailContext.userDetail])
  
  const initialUpdateState = useMemo(() => ({
    success: '',
    error: ''
  }), [])

  // State management
  const [mfaEnabled, setMfaEnabled] = useState(userDetailContext.userDetail.mfaEnabled)
  const [passwords, setPasswords] = useState(initialPasswordState)
  const [updateStatus, setUpdateStatus] = useState(initialUpdateState)
  const [updatedProfile, setUpdatedProfile] = useState(initialProfileState)
  const [formErrors, setFormErrors] = useState({})

  // Utility function to check for profile updates
  const hasProfileUpdates = useCallback((original, updated) => {
    return JSON.stringify(original) !== JSON.stringify(updated)
  }, [])

  // Update profile state when user details change
  useEffect(() => {
    setUpdatedProfile(prevState => ({
      ...prevState,
      hasUpdate: hasProfileUpdates(userDetailContext.userDetail, prevState.newUserDetail),
    }))
  }, [updatedProfile.newUserDetail, userDetailContext.userDetail, hasProfileUpdates])

  // Handle MFA changes
  useEffect(() => {
    handleProfileChange('mfaEnabled', mfaEnabled)
  }, [mfaEnabled])

  // Main update handler
  const handleUpdateProfile = useCallback(async () => {
    try {
      const publicKey = await getPublicKey(userDetailContext.userDetail.userId)
      const encPass = passwords.newPassword && btoa(await encrypt(passwords.newPassword, publicKey))
      const userPayload = {
        ...userDetailContext.userDetail,
        mfaEnabled,
        password: encPass
      }
      
      const response = await updateProfile(JSON.stringify(userPayload))
      
      if (response.ok) {
        const userResponse = await response.json()
        userDetailContext.updateUserDetail(userResponse)
        setUpdateStatus({
          success: 'Profile updated successfully!',
          error: ''
        })
      } else {
        setUpdateStatus({
          error: `Failed to update user profile. Status: ${response.status}`,
          success: ''
        })
      }
    } catch (error) {
      setUpdateStatus({
        error: 'Something went wrong, failed to update profile.',
        success: ''
      })
    }
  }, [userDetailContext, mfaEnabled, passwords.newPassword])

  // Profile field change handler
  const handleProfileChange = useCallback((field, value) => {
    setUpdatedProfile(prev => ({
      ...prev,
      newUserDetail: { ...prev.newUserDetail, [field]: value },
    }))
  }, [])

  // MFA toggle handler
  const handleMfaToggle = useCallback(() => {
    setMfaEnabled(prev => !prev)
  }, [])

  // Password reset toggle
  const passwordResetToggle = useCallback(() => {
    setPasswords(prevState => ({
      ...prevState,
      resetPassword: !prevState.resetPassword
    }))
  }, [])

  // Password update handler
  const handlePasswordUpdate = useCallback((type, value) => {
    setPasswords(prev => ({
      ...prev,
      [type === 'New' ? 'newPassword' : 'confirmPassword']: value
    }))
  }, [])

  // Error handling
  const setError = useCallback((name, value) => {
    setFormErrors(prevState => {
      if (!value) {
        const newState = { ...prevState }
        delete newState[name]
        return newState
      }
      return { ...prevState, [name]: value }
    })
  }, [])

  // Password validation
  const validatePassword = useCallback((name, value) => {
    if (!passwords.resetPassword) return

    if (name === 'New') {
      setError('newPassword', validPasswordFormat(value))
    }

    if (name === 'Confirm') {
      if (passwords.confirmPassword && passwords.newPassword) {
        if (passwords.confirmPassword !== passwords.newPassword) {
          setError('confirmPassword', 'Passwords do not match')
        } else {
          setError('confirmPassword', null)
          handleProfileChange('password', passwords.newPassword)
        }
      } else {
        setError('confirmPassword', 'Password should not be empty')
      }
    }
  }, [passwords.resetPassword, passwords.confirmPassword, passwords.newPassword, setError, handleProfileChange])

  // Modal close handler with cleanup
  const handleOnModalClose = useCallback(() => {
    setPasswords(initialPasswordState)
    setMfaEnabled(userDetailContext.userDetail.mfaEnabled)
    setUpdatedProfile(initialProfileState)
    setFormErrors({})
    setUpdateStatus(initialUpdateState)
    handleModalClose()
  }, [initialPasswordState, initialProfileState, initialUpdateState, userDetailContext.userDetail.mfaEnabled, handleModalClose])

  // Check if update button should be disabled
  const isUpdateDisabled = useMemo(() => {
    const hasErrors = Object.keys(formErrors).length > 0
    const hasPasswordErrors = passwords.resetPassword && (!passwords.newPassword || !passwords.confirmPassword)
    return !updatedProfile.hasUpdate || hasErrors || hasPasswordErrors
  }, [updatedProfile.hasUpdate, formErrors, passwords])

  return (
    <Modal
      open={isModalOpen}
      onClose={handleOnModalClose}
      aria-labelledby="profile-modal-title"
      aria-describedby="profile-modal-description"
    >
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          maxWidth: 600,
          maxHeight: '90vh',
          overflow: 'auto',
          p: 0,
        }}
        elevation={24}
      >
        {/* Modal Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            pb: 2,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderRadius: '4px 4px 0 0'
          }}
        >
          <Typography id="profile-modal-title" variant="h5" component="h2" fontWeight={600}>
            My Profile
          </Typography>
          <IconButton 
            onClick={handleOnModalClose}
            sx={{ color: 'inherit' }}
            aria-label="Close modal"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Status Messages */}
          {updateStatus.success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {updateStatus.success}
            </Alert>
          )}
          {updateStatus.error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {updateStatus.error}
            </Alert>
          )}

          {/* Profile Information */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom color="text.secondary" fontWeight={600}>
              Account Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" fontWeight={500} color="text.secondary">
                  Name:
                </Typography>
                <Typography variant="body1" fontWeight={400}>
                  {userDetailContext.userDetail.name}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" fontWeight={500} color="text.secondary">
                  User ID:
                </Typography>
                <Typography variant="body1" fontWeight={400}>
                  {userDetailContext.userDetail.userId}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" fontWeight={500} color="text.secondary">
                  Email:
                </Typography>
                <Typography variant="body1" fontWeight={400}>
                  {userDetailContext.userDetail.email}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Security Settings */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom color="text.secondary" fontWeight={600}>
              Security Settings
            </Typography>
            
            {/* MFA Setting */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, pl: 1 }}>
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  Multi-Factor Authentication
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add an extra layer of security to your account
                </Typography>
              </Box>
              <Switch 
                checked={mfaEnabled} 
                onChange={handleMfaToggle}
                color="primary"
              />
            </Box>

            {/* Password Reset Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pl: 1 }}>
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  Change Password
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Update your account password
                </Typography>
              </Box>
              <Switch 
                checked={passwords.resetPassword} 
                onChange={passwordResetToggle}
                color="primary"
              />
            </Box>

            {/* Password Fields */}
            {passwords.resetPassword && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, pl: 1 }}>
                <TextField
                  label="New Password"
                  type="password"
                  value={passwords.newPassword}
                  onChange={(e) => handlePasswordUpdate('New', e.target.value)}
                  fullWidth
                  onBlur={(e) => validatePassword('New', e.target.value)}
                  error={!!formErrors.newPassword}
                  helperText={formErrors.newPassword}
                  variant="outlined"
                  size="small"
                />
                <TextField
                  label="Confirm New Password"
                  type="password"
                  value={passwords.confirmPassword}
                  onChange={(e) => handlePasswordUpdate('Confirm', e.target.value)}
                  fullWidth
                  onBlur={(e) => validatePassword('Confirm', e.target.value)}
                  error={!!formErrors.confirmPassword}
                  helperText={formErrors.confirmPassword}
                  variant="outlined"
                  size="small"
                />
              </Box>
            )}
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Action Button */}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleUpdateProfile}
            disabled={isUpdateDisabled}
            size="large"
            sx={{ 
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1.1rem'
            }}
          >
            Update Profile
          </Button>
        </Box>
      </Paper>
    </Modal>
  )
}

export default ProfileModal
